import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, GlobalStyles, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import PrintIcon from '@mui/icons-material/Print';
import { errorText, type ShabbatVenue, type VendorMeal, type VendorRegistration } from '../../services/shabbat/shabbatService.ts';
import {
    MealTabs, NotHostingNote, ShabbatFrame, SincePicker, fmtDateTime, fmtInstant, isCombined, mealName, pickMeal, plural, shabbatService,
    useSince,
} from './shabbatShared.tsx';

/**
 * What the browser prints: only the sheet. The react-admin sidebar, the app
 * bar, the page header and the meal tabs are left out, and the content loses
 * the offset the fixed app bar needs on screen.
 */
const printStyles = (
    <GlobalStyles styles={{
        '@media print': {
            '@page': { margin: '14mm' },
            'html, body': { background: '#fff !important' },
            // The last one is the STAGING banner from utils/environment.ts.
            '.RaSidebar-root, .RaSidebar-fixed, .MuiDrawer-root, header, .MuiAppBar-root, .RaAppBar-root, [data-noprint], body > div[role="status"]': {
                display: 'none !important',
            },
            '.RaLayout-appFrame': { marginTop: '0 !important' },
            '.RaLayout-content, [data-print-root]': { padding: '0 !important', background: '#fff !important' },
            '[data-shabbat-frame]': { background: '#fff !important', minHeight: '0 !important', paddingBottom: '0 !important' },
            '[data-print-sheet]': { border: '0 !important', padding: '0 !important', maxWidth: 'none !important' },
            '[data-guest-row]': { breakInside: 'avoid' },
            // The layout fills the screen (react-admin's root ".layout" has
            // min-height 100vh) and the page sits 20px down for the STAGING
            // banner; on paper the two pushed an empty second page out after
            // a short list. Measured with print emulation, 15 Oct 2026.
            'html, body': { margin: '0 !important', padding: '0 !important' },
            'html, body, #root, .layout, .RaLayout-appFrame, .RaLayout-contentWithSidebar, .RaLayout-content, main, [data-print-root]': {
                height: 'auto !important', minHeight: '0 !important', overflow: 'visible !important',
            },
        },
    }} />
);

const COLUMNS = '40px 1.6fr 1.4fr minmax(160px, 1.2fr) 80px';

type Row = { n: number; name: string; booker: string; phone: string; note: string | null };

/**
 * One line per guest, families kept together, bookers in alphabetical order.
 * A registration for both meals carries the names of its largest party; when
 * fewer of them come to THIS meal, its first line says how many.
 */
const guestRows = (registrations: VendorRegistration[]): Row[] => {
    const rows: Row[] = [];
    registrations
        .filter(r => r.status === 'CONFIRMED')
        .slice()
        .sort((a, b) => a.bookedBy.localeCompare(b.bookedBy))
        .forEach(r => {
            const here = r.adults + r.children;
            const notes: string[] = [];
            if (isCombined(r)) notes.push('Dinner + lunch');
            if (here < r.guestNames.length) notes.push(`${here} of ${r.guestNames.length} at this meal`);
            r.guestNames.forEach((name, i) => rows.push({
                n: rows.length + 1, name, booker: r.bookedBy, phone: r.phone, note: i === 0 && notes.length ? notes.join(' · ') : null,
            }));
        });
    return rows;
};

const statusWord: Record<string, string> = { OPEN: 'still open', FULL: 'full', CLOSED: 'closed' };

const Sheet: React.FC<{ venue: ShabbatVenue; meal: VendorMeal; registrations: VendorRegistration[] }> = ({ venue, meal, registrations }) => {
    const confirmed = registrations.filter(r => r.status === 'CONFIRMED');
    const adults = confirmed.reduce((a, r) => a + r.adults, 0);
    const children = confirmed.reduce((a, r) => a + r.children, 0);
    const rows = guestRows(registrations);
    const printedAt = fmtInstant(new Date().toISOString(), venue.timeZone);

    return (
        <Box data-print-sheet="1" sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', px: 4.5, py: 4, maxWidth: 900, color: '#111' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '1px', color: '#6B7280', textTransform: 'uppercase' }}>Guest list</Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 700, mt: 0.5 }}>{mealName(meal.type)}</Typography>
            <Typography sx={{ fontSize: 15, color: '#374151', mt: 0.5 }}>{venue.name}{venue.address ? ` · ${venue.address}` : ''}</Typography>
            <Typography sx={{ fontSize: 15, color: '#374151', mt: 0.25 }}>{fmtDateTime(meal.date, meal.startTime)}</Typography>

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 2.25, px: 2, py: 1.5, bgcolor: '#F2FBF9', borderRadius: '10px', fontSize: 14, color: '#03676A' }}>
                <span style={{ fontWeight: 700 }}>{plural(adults + children, 'guest', 'guests')}</span>
                <span>{plural(adults, 'adult', 'adults')}</span>
                <span>{plural(children, 'child', 'children')}</span>
                <span>{plural(confirmed.length, 'party', 'parties')}</span>
            </Box>

            <Box sx={{
                display: 'grid', gridTemplateColumns: COLUMNS, gap: 1.5, px: 1.25, pt: 1.5, pb: 1, mt: 2.25,
                fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '.4px', borderBottom: '2px solid #111',
            }}>
                <span>#</span><span>Guest</span><span>Booked by</span><span>Phone</span><span style={{ textAlign: 'center' }}>Arrived</span>
            </Box>
            {rows.length === 0 && (
                <Typography sx={{ py: 4, textAlign: 'center', fontSize: 14, color: '#6B7280' }}>Nobody has registered yet.</Typography>
            )}
            {rows.map(g => (
                <Box key={g.n} data-guest-row="1" sx={{
                    display: 'grid', gridTemplateColumns: COLUMNS, gap: 1.5, px: 1.25, py: 1.1, alignItems: 'center',
                    borderBottom: '1px solid #E5E7EB', fontSize: 14,
                }}>
                    <span style={{ color: '#9CA3AF' }}>{g.n}</span>
                    <span style={{ fontWeight: 700 }}>{g.name}</span>
                    <span style={{ color: '#4B5563' }}>
                        {g.booker}
                        {g.note && <span style={{ display: 'block', fontSize: 11.5, color: '#6B7280' }}>{g.note}</span>}
                    </span>
                    <span style={{ direction: 'ltr', textAlign: 'start', color: '#4B5563' }}>{g.phone}</span>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Box sx={{ width: 18, height: 18, border: '1.5px solid #9CA3AF', borderRadius: '4px' }} />
                    </Box>
                </Box>
            ))}
            <Typography sx={{ fontSize: 12, color: '#9CA3AF', mt: 2.25 }}>
                Printed from Vello · {printedAt} · Registration {statusWord[meal.status] ?? meal.status.toLowerCase()}
            </Typography>
        </Box>
    );
};

const VenueGuestList: React.FC<{ venue: ShabbatVenue }> = ({ venue }) => {
    const [params, setParams] = useSearchParams();
    const [meals, setMeals] = useState<VendorMeal[] | null>(null);
    const [loaded, setLoaded] = useState<{ meal: VendorMeal; registrations: VendorRegistration[] } | null>(null);
    const [failed, setFailed] = useState<string | null>(null);
    const { from } = useSince();

    useEffect(() => {
        let alive = true;
        setMeals(null);
        shabbatService.meals(venue.id, from)
            .then(list => { if (alive) setMeals(list); })
            .catch(e => { if (alive) setFailed(errorText(e, 'Could not load the meals.')); });
        return () => { alive = false; };
    }, [venue.id, from]);

    const selected = meals ? pickMeal(meals, Number(params.get('meal')) || null, venue.timeZone) : null;

    const load = useCallback(async (mealId: number) => {
        try {
            setLoaded(await shabbatService.registrations(mealId));
        } catch (e) {
            setFailed(errorText(e, 'Could not load the guest list.'));
        }
    }, []);

    useEffect(() => { setLoaded(null); if (selected) load(selected); }, [selected, load]);

    if (failed) return <Alert severity="error" sx={{ maxWidth: 760 }}>{failed}</Alert>;
    if (!meals) return <CircularProgress size={24} />;
    if (meals.length === 0) return (
        <>
            <Box data-noprint="1"><NotHostingNote venue={venue} /></Box>
            <Alert severity="info" sx={{ maxWidth: 640 }}>
                {from ? 'No meals in this period.' : venue.hostsShabbat ? 'No meals yet. Add one on the Shabbat meals page.' : 'No meals this Shabbat or later.'}
            </Alert>
        </>
    );

    return (
        <>
            <Box data-noprint="1"><NotHostingNote venue={venue} /></Box>
            <Box data-noprint="1" sx={{ mb: 2.25 }}>
                <MealTabs meals={meals} selected={selected} onPick={id => setParams(p => { const next = new URLSearchParams(p); next.set('meal', String(id)); return next; })} />
            </Box>
            {!loaded ? <CircularProgress size={24} /> : <Sheet venue={venue} meal={loaded.meal} registrations={loaded.registrations} />}
        </>
    );
};

/** The list for the door: print it on Friday. */
const ShabbatGuestListPage = () => (
    <>
        {printStyles}
        <ShabbatFrame
            title="Guest list"
            subtitle="Print it for the door on Friday"
            actions={() => (
                <>
                <SincePicker />
                <Button variant="contained" color="secondary" startIcon={<PrintIcon />} onClick={() => window.print()}
                    sx={{ borderRadius: '20px', color: '#fff', height: 40, px: 2.25 }}>
                    Print guest list
                </Button>
                </>
            )}
        >
            {(venue) => venue && <VenueGuestList key={venue.id} venue={venue} />}
        </ShabbatFrame>
    </>
);

export default ShabbatGuestListPage;
